import mobileExportColumns from 'components/tables/mobile-export-columns';
import { Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPencil } from '@tabler/icons-react';
import { ColDef } from 'ag-grid-community';
import { HrBulkUpdatePeopleEvidencePayload } from 'api/actions/hr-bulk-update-people-evidence/hr-bulk-update-people-evidence-payload';
import { HrListPeopleResponse } from 'api/actions/hr-list-people/hr-list-people-response';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import DataTable from 'components/tables/DataTable';
import { DataTableExport } from 'components/tables/ExportModal';
import SimpleValueFormatter from 'components/tables/SimpleValueFormatter';
import HrPeopleBulkEvidenceModal, {
  HrBulkEvidenceDefinition,
  HrBulkEvidenceTarget,
} from 'components/tables/data/hr/HrPeopleBulkEvidenceModal';
import { getEvidenceFieldValue } from 'utils/evidence-field';
import { hrActionCellVersion, HrPersonActions } from 'components/tables/data/hr/HrActions';
import {
  HrIdCell,
  HrOrganizationCell,
  HrPersonIdentityCell,
  HrPersonNameCell,
  HrTextCell,
  HrUserCell,
} from 'components/tables/data/hr/HrTableCells';
import { HrLinkedUserSelectFilter } from 'components/tables/filters/hr/HrEntitySelectFilters';
import useObjectColumn from 'components/tables/hierarchy/ObjectColumn';
import OrganizationSelectFilter from 'components/tables/filters/OrganizationSelectFilter';
import panic from 'errors/panic';
import {
  getResponsiveActionsColumnWidth,
  SINGLE_ACTIONS_COLUMN_WIDTH,
  useResponsiveDesign,
} from 'hooks/use-responsive-design';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HR_CREATE_PERSON_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

export type HrPerson = HrListPeopleResponse[number];
export type HrEvidenceField = HrPerson['evidenceFields'][number] & { precision?: number };
export type HrPersonRow = Omit<HrPerson, 'evidenceFields'> & {
  evidenceFields: Record<string, string | number | boolean>;
  evidenceDefinitions: HrEvidenceField[];
  linkedUserId: number | null;
  rawStatus: string;
  _identity: string;
  _lifecycleActive: string;
};

interface HrBulkEvidenceSelection {
  people: HrBulkEvidenceTarget[];
  evidenceFields: HrBulkEvidenceDefinition[];
  hasExcludedEvidenceFields: boolean;
}

const advancedEvidenceFilterIcon =
  '<div title="Pokročilé filtrovanie"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-filter-spark"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M15 12.5v-.5l4.414 -4.414a2 2 0 0 0 .586 -1.414v-2.172h-16v2.227c0 .497 .185 .977 .52 1.345l4.48 4.928v8.5l2 -.667" /><path d="M18.5 22a4.75 4.75 0 0 1 3.5 -3.5a4.75 4.75 0 0 1 -3.5 -3.5a4.75 4.75 0 0 1 -3.5 3.5a4.75 4.75 0 0 1 3.5 3.5" /></svg></div>';

/** Returns the first person's definition order, restricted to definitions shared by every selected person. */
export function commonHrEvidenceDefinitions(people: HrPersonRow[]): HrBulkEvidenceDefinition[] {
  const [firstPerson, ...otherPeople] = people;
  if (!firstPerson) return [];

  return firstPerson.evidenceDefinitions.filter((definition) =>
    otherPeople.every((person) =>
      person.evidenceDefinitions.some((candidate) => candidate.evidenceFieldId === definition.evidenceFieldId)
    )
  );
}

/** Only active rows with an explicit edit capability may participate in a bulk evidence update. */
export function canBulkEditHrPeopleEvidence(people: HrPersonRow[]) {
  return (
    people.length > 0 &&
    people.every((person) => person.rawStatus === 'active' && person.actionPermissions.edit === true) &&
    commonHrEvidenceDefinitions(people).length > 0
  );
}

/** People list with saved profiles, filters, retained-row toggle, lifecycle actions, and XLSX export. */
export default function HrPeopleTable() {
  const { getAction, hasPermissionAnywhere } = useApi();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const [refreshToken, setRefreshToken] = useState(0);
  const [evidenceFieldsColumns, setEvidenceFieldsColumns] = useState<ColDef[]>([]);
  const [people, setPeople] = useState<HrPersonRow[]>([]);
  const [selectedRows, setSelectedRows] = useState<HrPersonRow[]>([]);
  const [bulkEvidenceSelection, setBulkEvidenceSelection] = useState<HrBulkEvidenceSelection | null>(null);
  const [loading, setLoading] = useState(true);
  const loadRequestId = useRef(0);
  const objectColumn = useObjectColumn({
    breadcrumbField: 'departmentBreadcrumbs',
    organizationFilterKey: 'organizationId',
    permissionSlug: 'browse-hr',
    legacyColumnIds: ['departmentId'],
  });
  const fetchPeople = useCallback(async () => {
    const requestId = ++loadRequestId.current;

    const listPeople = getAction('HrListPeople');
    const response = await listPeople({
      query: { filters: { includeInactive: true } },
    });

    const evidenceFieldMap = new Map<string, HrEvidenceField>();
    const rows = response.map((person) => {
      const evidenceFields = person.evidenceFields.reduce((acc, sourceField) => {
        const field = sourceField as HrEvidenceField;
        if (!field.fieldName || !field.evidenceFieldId) {
          return acc;
        }

        const key = String(field.evidenceFieldId);
        const value = getEvidenceFieldValue(field) ?? '';

        evidenceFieldMap.set(key, field);
        acc[key] = value;

        return acc;
      }, {} as Record<string, string | number | boolean>);

      return {
        ...person,
        evidenceFields,
        evidenceDefinitions: person.evidenceFields.map((field) => ({ ...field })) as HrEvidenceField[],
        rawStatus: person.status,
        _lifecycleActive: person.status === 'active' ? '1' : '0',
        _identity: `${person.firstName} ${person.lastName} ID ${person.personId}`,
        linkedUserId: person.userId ?? null,
      };
    });

    if (requestId !== loadRequestId.current) {
      return false;
    }

    setEvidenceFieldsColumns(
      Array.from(evidenceFieldMap).map(([key, { fieldName, precision = 0 }]) => ({
        field: `evidenceFields.${key}`,
        headerName: fieldName,
        minWidth: 200,
        sortable: true,
        unSortIcon: true,
        filter: true,
        floatingFilter: true,
        filterValueGetter: ({ data }) => data?.evidenceFields?.[key],
        cellRenderer: ({ data }: { data?: HrPersonRow }) => (
          <SimpleValueFormatter value={data?.evidenceFields?.[key]} precision={precision} />
        ),
        icons: { filter: () => advancedEvidenceFilterIcon },
      }))
    );
    setPeople(rows);
    setLoading(false);

    return true;
  }, [getAction]);

  const action = useCallback(() => Promise.resolve(people), [people]);

  useEffect(() => {
    void fetchPeople().catch(panic);

    return () => {
      loadRequestId.current += 1;
    };
  }, [fetchPeople]);

  const refreshPeople = useCallback(async () => {
    if (await fetchPeople()) {
      setRefreshToken((value) => value + 1);
    }
  }, [fetchPeople]);

  const commonSelectedEvidenceFields = useMemo(() => commonHrEvidenceDefinitions(selectedRows), [selectedRows]);
  const canBulkEditSelectedPeople = useMemo(() => canBulkEditHrPeopleEvidence(selectedRows), [selectedRows]);

  const openBulkEvidenceEdit = useCallback(() => {
    if (!canBulkEditSelectedPeople) return;

    const commonEvidenceFieldIds = new Set(commonSelectedEvidenceFields.map((field) => field.evidenceFieldId));
    setBulkEvidenceSelection({
      people: selectedRows.map(({ personId, version, firstName, lastName }) => ({
        personId,
        version,
        firstName,
        lastName,
      })),
      evidenceFields: commonSelectedEvidenceFields.map((field) => ({
        ...field,
        selectOptions: [...(field.selectOptions ?? [])],
      })),
      hasExcludedEvidenceFields: selectedRows.some((person) =>
        person.evidenceDefinitions.some((field) => !commonEvidenceFieldIds.has(field.evidenceFieldId))
      ),
    });
  }, [canBulkEditSelectedPeople, commonSelectedEvidenceFields, selectedRows]);

  const submitBulkEvidenceEdit = useCallback(
    async (evidenceFields: HrBulkUpdatePeopleEvidencePayload['evidenceFields']) => {
      if (!bulkEvidenceSelection) return;

      const updatePeopleEvidence = getAction('HrBulkUpdatePeopleEvidence');
      const response = await updatePeopleEvidence({
        payload: {
          people: bulkEvidenceSelection.people.map(({ personId, version }) => ({ personId, version })),
          evidenceFields,
        },
      });

      await refreshPeople();
      setSelectedRows([]);
      setBulkEvidenceSelection(null);
      notifications.show({
        title: 'Evidenčné polia boli upravené',
        message: `Aktualizovaných osôb: ${response.count}.`,
        color: SUCCESS_NOTIFICATION_COLOR,
      });
    },
    [bulkEvidenceSelection, getAction, refreshPeople]
  );

  const columns = useMemo<ColDef[]>(
    () =>
      isMobile
        ? [
            {
              field: '_identity',
              headerName: 'Osoba',
              pinned: 'left',
              lockPosition: 'left',
              minWidth: 140,
              flex: 1,
              autoHeight: true,
              autoHeaderHeight: true,
              cellRenderer: HrPersonIdentityCell,
              getQuickFilterText: ({ data }) =>
                [data?.firstName, data?.lastName, data?.userFullName, data?.organizationName, data?.departmentName]
                  .filter(Boolean)
                  .join(' '),
            },
            {
              field: '_lifecycleActive',
              headerName: 'Stav',
              hide: true,
              filter: true,
            },
            ...mobileExportColumns([
              { field: 'personId', headerName: 'ID', hide: true },
              { field: 'firstName', headerName: 'Meno', hide: true },
              { field: 'lastName', headerName: 'Priezvisko', hide: true },
              {
                field: 'linkedUserId',
                headerName: 'Prepojený používateľ',
                hide: true,
                valueFormatter: ({ data }) => data?.userFullName ?? '',
              },
              {
                field: 'organizationId',
                headerName: 'Organizácia',
                hide: true,
                valueFormatter: ({ data }) => data?.organizationName ?? '',
              },
            ]),
            ...objectColumn.columns.map((column) => ({ ...column, hide: true })),
            ...evidenceFieldsColumns.map((column) => ({ ...column, hide: true })),
            {
              field: '_actions',
              headerName: 'Akcie',
              valueGetter: hrActionCellVersion,
              pinned: 'right',
              width: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
              minWidth: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
              maxWidth: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
              autoHeaderHeight: true,
              cellRenderer: HrPersonActions,
              cellRendererParams: { onStatusChange: refreshPeople },
            },
          ]
        : [
            {
              field: 'personId',
              headerName: 'ID',
              width: 76,
              minWidth: 76,
              maxWidth: 76,
              resizable: false,
              sortable: true,
              unSortIcon: true,
              autoHeaderHeight: true,
              cellRenderer: HrIdCell,
            },
            {
              field: 'firstName',
              headerName: 'Meno',
              minWidth: 180,
              sortable: true,
              unSortIcon: true,
              autoHeaderHeight: true,
              cellRenderer: HrPersonNameCell,
            },
            {
              field: 'lastName',
              headerName: 'Priezvisko',
              minWidth: 200,
              sortable: true,
              unSortIcon: true,
              autoHeaderHeight: true,
              cellRenderer: HrTextCell,
            },
            {
              field: 'linkedUserId',
              headerName: 'Prepojený používateľ',
              minWidth: 260,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: HrLinkedUserSelectFilter,
              autoHeaderHeight: true,
              cellRenderer: HrUserCell,
              valueFormatter: ({ data }) => data?.userFullName ?? '',
              getQuickFilterText: ({ data }) => data?.userFullName ?? '',
            },
            {
              field: 'organizationId',
              headerName: 'Organizácia',
              minWidth: 240,
              sortable: true,
              unSortIcon: true,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: OrganizationSelectFilter.configure({
                filterKey: 'organizationId',
                permissionSlug: 'browse-hr',
              }),
              autoHeaderHeight: true,
              cellRenderer: HrOrganizationCell,
              valueFormatter: ({ data }) => data?.organizationName ?? '',
              getQuickFilterText: ({ data }) => data?.organizationName ?? '',
            },
            ...objectColumn.columns,
            {
              field: '_lifecycleActive',
              headerName: 'Stav',
              hide: true,
              filter: true,
            },
            ...evidenceFieldsColumns,
            {
              field: '_actions',
              headerName: 'Akcie',
              valueGetter: hrActionCellVersion,
              pinned: 'right',
              width: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
              minWidth: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
              maxWidth: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
              autoHeaderHeight: true,
              cellRenderer: HrPersonActions,
              cellRendererParams: { onStatusChange: refreshPeople },
            },
          ],
    [evidenceFieldsColumns, objectColumn.columns, isMobile, refreshPeople, tableActionMode]
  );

  const exportColumns = useMemo(
    () => [
      'personId',
      'firstName',
      'lastName',
      'linkedUserId',
      'organizationId',
      ...objectColumn.columnKeys,
      ...evidenceFieldsColumns.map(({ field }) => field ?? '').filter(Boolean),
    ],
    [evidenceFieldsColumns, objectColumn.columnKeys]
  );

  const dataExport: DataTableExport<HrPersonRow> = useMemo(
    () => ({
      modalTitle: 'Exportovať osoby',
      fileName: 'hr-osoby.xlsx',
      columnKeys: exportColumns,
      columnReplacements: { ...objectColumn.columnReplacements, _identity: ['personId', 'firstName', 'lastName'] },
      ready: objectColumn.exportReady,
      error: objectColumn.exportError,
      initialSelectedColumns: (rows) =>
        exportColumns.filter((column) => {
          if (column.startsWith('evidenceFields.')) {
            const fieldKey = column.replace('evidenceFields.', '');
            return rows.some((row) => {
              const value = row.evidenceFields?.[fieldKey];
              return value !== undefined && value !== null && value !== '';
            });
          }

          return true;
        }),
    }),
    [exportColumns, objectColumn.columnReplacements, objectColumn.exportError, objectColumn.exportReady]
  );

  if (loading) return null;

  return (
    <>
      <DataTable
        tableId="hr-people"
        title="Osoby"
        toggleDiscardedLabel="Zobraziť vyradené"
        discardedFilterField="_lifecycleActive"
        dataRefreshToken={refreshToken}
        addButtonText="Pridať osobu"
        addButtonTarget={HR_CREATE_PERSON_PAGE_PATH.original}
        hideAddButton={!hasPermissionAnywhere('manage-hr')}
        columns={columns}
        action={action}
        selectedRows={selectedRows}
        onSelectedRowsChange={setSelectedRows}
        bulkActions={
          canBulkEditSelectedPeople ? (
            <Group spacing={8} noWrap>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<IconPencil size={18} stroke={1.5} />}
                onClick={openBulkEvidenceEdit}
              >
                Upraviť evidenčné polia ({selectedRows.length})
              </Button>
            </Group>
          ) : null
        }
        getRowId={({ data }) => String(data.personId)}
        dataExport={dataExport}
        profileColumnAliases={objectColumn.profileColumnAliases}
        profileMigrationReady={objectColumn.profileMigrationReady}
      />
      {bulkEvidenceSelection && (
        <HrPeopleBulkEvidenceModal
          opened
          people={bulkEvidenceSelection.people}
          evidenceFields={bulkEvidenceSelection.evidenceFields}
          hasExcludedEvidenceFields={bulkEvidenceSelection.hasExcludedEvidenceFields}
          onClose={() => setBulkEvidenceSelection(null)}
          onSubmit={submitBulkEvidenceEdit}
        />
      )}
    </>
  );
}
